import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Axios from 'axios'
import { Link } from 'react-router-dom'
import MUIDataTable from 'mui-datatables'
import { format, subDays, startOfYear } from 'date-fns'
import {
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { alpha, styled } from '@mui/system'
import PaidIcon from '@mui/icons-material/Paid'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import PercentIcon from '@mui/icons-material/Percent'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import DownloadIcon from '@mui/icons-material/Download'
import InboxIcon from '@mui/icons-material/Inbox'
import { API_URI } from '../../../../config'

const SEARCH_DEBOUNCE_MS = 400

const STATUS_CONFIG = {
    succeeded: { label: 'Succeeded', color: '#10b981' },
    pending: { label: 'Pending', color: '#f59e0b' },
    failed: { label: 'Failed', color: '#ef4444' },
}

const DATE_PRESETS = [
    { id: '7d', label: 'Last 7 days' },
    { id: '30d', label: 'Last 30 days' },
    { id: '90d', label: 'Last 90 days' },
    { id: 'ytd', label: 'Year to date' },
    { id: 'all', label: 'All time' },
    { id: 'custom', label: 'Custom' },
]

const Wrapper = styled(Box)(({ theme }) => ({
    maxWidth: 1280,
    margin: `${theme.spacing(3)} auto 0`,
    padding: theme.spacing(0, 1, 4),
}))

const HeroStat = styled(Card)(({ theme }) => ({
    padding: '24px',
    height: '100%',
    borderRadius: '16px',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
}))

const FeePanel = styled(Card)(({ theme }) => ({
    borderRadius: '16px',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    overflow: 'hidden',
}))

const FeeCell = styled(Box)(({ theme }) => ({
    padding: '20px 24px',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
        padding: '16px 20px',
    },
}))

const IconWrapper = styled(Box)(({ bgcolor }) => ({
    width: 48,
    height: 48,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: bgcolor || 'rgba(16, 185, 129, 0.1)',
    flexShrink: 0,
}))

const ChartCard = styled(Card)(({ theme }) => ({
    padding: '20px 24px 16px',
    height: '100%',
    borderRadius: '12px',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
}))

const TableCard = styled(Card)(({ theme }) => ({
    borderRadius: '12px',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    overflow: 'auto',
    '& .MuiPaper-root': {
        boxShadow: 'none',
    },
}))

const StatusChip = styled(Chip)(({ statuscolor }) => ({
    height: '26px',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
    background: alpha(statuscolor || '#64748b', 0.12),
    color: statuscolor || '#64748b',
    border: `1px solid ${alpha(statuscolor || '#64748b', 0.2)}`,
}))

function useDebouncedValue(value, delay = SEARCH_DEBOUNCE_MS) {
    const [debouncedValue, setDebouncedValue] = useState(value)
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedValue(value), delay)
        return () => clearTimeout(timer)
    }, [value, delay])
    return debouncedValue
}

const toIsoDate = (date) => format(date, 'yyyy-MM-dd')

const rangeFromPreset = (preset) => {
    const today = new Date()
    const to = toIsoDate(today)
    if (preset === '7d') return { from: toIsoDate(subDays(today, 6)), to }
    if (preset === '30d') return { from: toIsoDate(subDays(today, 29)), to }
    if (preset === '90d') return { from: toIsoDate(subDays(today, 89)), to }
    if (preset === 'ytd') return { from: toIsoDate(startOfYear(today)), to }
    return { from: '', to: '' }
}

const formatMoney = (amount, currency = 'AUD') => {
    try {
        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: currency || 'AUD',
        }).format(Number(amount) || 0)
    } catch {
        return `$${(Number(amount) || 0).toFixed(2)}`
    }
}

const percent = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0)


const PLAN_COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899', '#64748b']

const PlanPieChart = ({ items, currency }) => {
    const slices = items.filter((item) => (item.amount || 0) > 0)
    const total = slices.reduce((sum, item) => sum + (item.amount || 0), 0)

    if (!slices.length || total <= 0) {
        return (
            <Typography variant="body2" color="text.secondary" textAlign="center">
                No plan revenue in this range
            </Typography>
        )
    }

    const radius = 54
    const circumference = 2 * Math.PI * radius
    let dashOffset = 0

    return (
        <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
            <Box sx={{ position: 'relative', width: 220, height: 220 }}>
                <Box
                    component="svg"
                    viewBox="0 0 160 160"
                    sx={{ width: 220, height: 220, display: 'block' }}
                    role="img"
                    aria-label="Revenue by membership plan"
                >
                    <g transform="rotate(-90 80 80)">
                        {slices.map((item, index) => {
                            const dash = (item.amount / total) * circumference
                            const circle = (
                                <circle
                                    key={item.key}
                                    cx="80"
                                    cy="80"
                                    r={radius}
                                    fill="none"
                                    stroke={PLAN_COLORS[index % PLAN_COLORS.length]}
                                    strokeWidth="24"
                                    strokeDasharray={`${dash} ${circumference - dash}`}
                                    strokeDashoffset={-dashOffset}
                                >
                                    <title>
                                        {`${item.name}: ${formatMoney(item.amount, currency)} (${percent(item.amount, total)}%)`}
                                    </title>
                                </circle>
                            )
                            dashOffset += dash
                            return circle
                        })}
                    </g>
                </Box>
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography variant="caption" color="text.secondary">
                        Gross
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {formatMoney(total, currency)}
                    </Typography>
                </Box>
            </Box>
            <Box
                display="flex"
                flexWrap="wrap"
                justifyContent="center"
                gap={1.5}
                maxWidth={560}
            >
                {slices.map((item, index) => (
                    <Box
                        key={item.key}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ minWidth: 160 }}
                    >
                        <Box
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: PLAN_COLORS[index % PLAN_COLORS.length],
                                flexShrink: 0,
                            }}
                        />
                        <Box>
                            <Typography variant="body2" fontWeight={600} noWrap>
                                {item.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {formatMoney(item.amount, currency)} · {percent(item.amount, total)}%
                                {item.count != null ? ` · ${item.count}` : ''}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

const ClubTransactions = () => {
    const initialRange = rangeFromPreset('30d')
    const [loading, setLoading] = useState(true)
    const [exporting, setExporting] = useState(false)
    const [transactions, setTransactions] = useState([])
    const [preset, setPreset] = useState('30d')
    const [from, setFrom] = useState(initialRange.from)
    const [to, setTo] = useState(initialRange.to)
    const [status, setStatus] = useState('')
    const [minAmount, setMinAmount] = useState('')
    const [maxAmount, setMaxAmount] = useState('')
    const [searchValue, setSearchValue] = useState('')
    const [tableConfig, setTableConfig] = useState({
        page: 0,
        rowsPerPage: 10,
        count: 0,
    })
    const [stats, setStats] = useState({
        total: 0,
        succeeded: 0,
        pending: 0,
        failed: 0,
        revenue: 0,
        netRevenue: 0,
        stripeFee: 0,
        stripeTax: 0,
        applicationFee: 0,
        pendingAmount: 0,
        failedAmount: 0,
        average: 0,
        currency: 'AUD',
    })
    const [planBreakdown, setPlanBreakdown] = useState([])
    const debouncedSearch = useDebouncedValue(searchValue)
    const debouncedMin = useDebouncedValue(minAmount)
    const debouncedMax = useDebouncedValue(maxAmount)

    const buildParams = useCallback(
        (page, rowsPerPage) => {
            const params = new URLSearchParams({
                page: String(page + 1),
                limit: String(rowsPerPage),
            })
            if (from) params.set('from', from)
            if (to) params.set('to', to)
            if (status) params.set('status', status)
            if (debouncedMin !== '') params.set('minAmount', debouncedMin)
            if (debouncedMax !== '') params.set('maxAmount', debouncedMax)
            if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim())
            return params
        },
        [from, to, status, debouncedMin, debouncedMax, debouncedSearch]
    )

    const fetchData = useCallback(
        async (page = 0, rowsPerPage = 10) => {
            try {
                setLoading(true)
                const { data } = await Axios.get(
                    `${API_URI}/api/transactions?${buildParams(page, rowsPerPage).toString()}`,
                    {
                        headers: {
                            authorization: `Bearer ${window.localStorage.getItem('accessToken')}`,
                        },
                    }
                )
                setTransactions(data?.transactions || [])
                setStats(data?.stats || {})
                setPlanBreakdown(data?.planBreakdown || [])
                setTableConfig({
                    page,
                    rowsPerPage,
                    count: data?.pagination?.totalCount || 0,
                })
            } catch (error) {
                console.error('Error fetching club transactions:', error)
            } finally {
                setLoading(false)
            }
        },
        [buildParams]
    )

    useEffect(() => {
        fetchData(0, tableConfig.rowsPerPage)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchData])

    const handlePreset = (nextPreset) => {
        setPreset(nextPreset)
        if (nextPreset === 'custom') return
        const range = rangeFromPreset(nextPreset)
        setFrom(range.from)
        setTo(range.to)
    }

    const handleExport = async () => {
        try {
            setExporting(true)
            const params = buildParams(0, tableConfig.rowsPerPage)
            params.delete('page')
            params.delete('limit')
            const query = params.toString()
            const { data } = await Axios.get(
                `${API_URI}/api/transactions/export${query ? `?${query}` : ''}`,
                {
                    headers: {
                        authorization: `Bearer ${window.localStorage.getItem('accessToken')}`,
                    },
                    responseType: 'blob',
                }
            )
            const url = window.URL.createObjectURL(new Blob([data], { type: 'text/csv' }))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `club-transactions-${toIsoDate(new Date())}.csv`)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Transaction export failed:', error)
        } finally {
            setExporting(false)
        }
    }

    const currency = stats.currency || 'AUD'
    const totalFees =
        (Number(stats.stripeFee) || 0) +
        (Number(stats.stripeTax) || 0) +
        (Number(stats.applicationFee) || 0)

    const headlineStats = [
        {
            title: 'Gross revenue',
            value: formatMoney(stats.revenue, currency),
            helper: `${stats.succeeded || 0} succeeded payments`,
            icon: PaidIcon,
            color: '#3b82f6',
            bg: 'rgba(59, 130, 246, 0.1)',
        },
        {
            title: 'Net to club',
            value: formatMoney(stats.netRevenue, currency),
            helper: `${formatMoney(totalFees, currency)} deducted in fees`,
            icon: AccountBalanceWalletIcon,
            color: '#10b981',
            bg: 'rgba(16, 185, 129, 0.1)',
        },
    ]

    const feeStats = [
        {
            title: 'Stripe fee',
            value: formatMoney(stats.stripeFee, currency),
            helper: 'Processing',
            icon: ReceiptLongIcon,
            color: '#64748b',
            bg: 'rgba(100, 116, 139, 0.1)',
        },
        {
            title: 'GST / tax',
            value: formatMoney(stats.stripeTax, currency),
            helper: 'On Stripe fees',
            icon: PercentIcon,
            color: '#f59e0b',
            bg: 'rgba(245, 158, 11, 0.1)',
        },
        {
            title: 'Application fee',
            value: formatMoney(stats.applicationFee, currency),
            helper: 'CDM platform',
            icon: AccountBalanceIcon,
            color: '#8b5cf6',
            bg: 'rgba(139, 92, 246, 0.1)',
        },
    ]

    const columns = useMemo(
        () => [
            {
                name: 'createdAt',
                label: 'Date',
                options: {
                    filter: false,
                    sort: false,
                    customBodyRenderLite: (dataIndex) => {
                        const row = transactions[dataIndex]
                        if (!row?.createdAt) return '-'
                        return (
                            <Box>
                                <Typography variant="body2" fontWeight={600}>
                                    {format(new Date(row.createdAt), 'dd MMM yyyy')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {format(new Date(row.createdAt), 'hh:mm a')}
                                </Typography>
                            </Box>
                        )
                    },
                },
            },
            {
                name: 'member',
                label: 'Member',
                options: {
                    filter: false,
                    sort: false,
                    setCellHeaderProps: () => ({
                        style: { width: '22%', minWidth: 180 },
                    }),
                    setCellProps: () => ({
                        style: { width: '22%', minWidth: 180 },
                    }),
                    customBodyRenderLite: (dataIndex) => {
                        const row = transactions[dataIndex]
                        const name = row?.member?.name || row?.member?.email || '-'
                        const body = (
                            <Box>
                                <Typography variant="body2" fontWeight={600}>
                                    {name}
                                </Typography>
                                {row?.member?.name ? (
                                    <Typography variant="caption" color="text.secondary">
                                        {row.member.email}
                                    </Typography>
                                ) : null}
                            </Box>
                        )
                        if (!row?.member?._id) return body
                        return (
                            <Box
                                component={Link}
                                to={`/pages/view-customer/${row.member._id}`}
                                sx={{ textDecoration: 'none', color: 'inherit' }}
                            >
                                {body}
                            </Box>
                        )
                    },
                },
            },
            {
                name: 'plan',
                label: 'Membership Plan',
                options: {
                    filter: false,
                    sort: false,
                    setCellHeaderProps: () => ({
                        style: { width: '16%', minWidth: 140 },
                    }),
                    setCellProps: () => ({
                        style: { width: '16%', minWidth: 140 },
                    }),
                    customBodyRenderLite: (dataIndex) => (
                        <Typography variant="body2">
                            {transactions[dataIndex]?.plan?.name || '-'}
                        </Typography>
                    ),
                },
            },
            {
                name: 'amount',
                label: 'Amount',
                options: {
                    filter: false,
                    sort: false,
                    setCellHeaderProps: () => ({
                        style: { width: 88, whiteSpace: 'nowrap' },
                    }),
                    setCellProps: () => ({
                        style: { width: 88, whiteSpace: 'nowrap' },
                    }),
                    customBodyRenderLite: (dataIndex) => {
                        const row = transactions[dataIndex]
                        return (
                            <Typography variant="body2" fontWeight={700} noWrap>
                                {formatMoney(row?.amount, row?.currency || currency)}
                            </Typography>
                        )
                    },
                },
            },
            {
                name: 'stripe_fee',
                label: 'Stripe fee',
                options: {
                    filter: false,
                    sort: false,
                    setCellHeaderProps: () => ({
                        style: { width: 88, whiteSpace: 'nowrap' },
                    }),
                    setCellProps: () => ({
                        style: { width: 88, whiteSpace: 'nowrap' },
                    }),
                    customBodyRenderLite: (dataIndex) => (
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {formatMoney(
                                transactions[dataIndex]?.stripe_fee,
                                transactions[dataIndex]?.currency || currency
                            )}
                        </Typography>
                    ),
                },
            },
            {
                name: 'stripe_tax',
                label: 'Tax',
                options: {
                    filter: false,
                    sort: false,
                    setCellHeaderProps: () => ({
                        style: { width: 80, whiteSpace: 'nowrap' },
                    }),
                    setCellProps: () => ({
                        style: { width: 80, whiteSpace: 'nowrap' },
                    }),
                    customBodyRenderLite: (dataIndex) => (
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {formatMoney(
                                transactions[dataIndex]?.stripe_tax,
                                transactions[dataIndex]?.currency || currency
                            )}
                        </Typography>
                    ),
                },
            },
            {
                name: 'cdm_application_fee',
                label: 'App fee',
                options: {
                    filter: false,
                    sort: false,
                    setCellHeaderProps: () => ({
                        style: { width: 88, whiteSpace: 'nowrap' },
                    }),
                    setCellProps: () => ({
                        style: { width: 88, whiteSpace: 'nowrap' },
                    }),
                    customBodyRenderLite: (dataIndex) => (
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {formatMoney(
                                transactions[dataIndex]?.cdm_application_fee,
                                transactions[dataIndex]?.currency || currency
                            )}
                        </Typography>
                    ),
                },
            },
            {
                name: 'net_amount',
                label: 'Net',
                options: {
                    filter: false,
                    sort: false,
                    setCellHeaderProps: () => ({
                        style: { width: 88, whiteSpace: 'nowrap' },
                    }),
                    setCellProps: () => ({
                        style: { width: 88, whiteSpace: 'nowrap' },
                    }),
                    customBodyRenderLite: (dataIndex) => {
                        const row = transactions[dataIndex]
                        return (
                            <Typography variant="body2" fontWeight={700} noWrap color="success.main">
                                {formatMoney(row?.net_amount, row?.currency || currency)}
                            </Typography>
                        )
                    },
                },
            },
            {
                name: 'status',
                label: 'Status',
                options: {
                    filter: false,
                    sort: false,
                    setCellHeaderProps: () => ({
                        style: { 
                            width: 150, 
                            maxWidth: 150, 
                            whiteSpace: 'nowrap' 
                        },
                    }),
                    setCellProps: () => ({
                        style: { 
                            width: 150, 
                            maxWidth: 150, 
                            whiteSpace: 'nowrap' 
                        },
                    }),
                    customBodyRenderLite: (dataIndex) => {
                        const row = transactions[dataIndex]
                        const config = STATUS_CONFIG[row?.status] || STATUS_CONFIG.pending
                        return (
                            <StatusChip
                                label={config.label}
                                size="small"
                                statuscolor={config.color}
                            />
                        )
                    },
                },
            }
        ],
        [transactions, currency]
    )

    const tableOptions = {
        filterType: 'dropdown',
        selectableRows: 'none',
        download: false,
        filter: false,
        print: false,
        search: false,
        viewColumns: false,
        elevation: 0,
        serverSide: true,
        rowsPerPage: tableConfig.rowsPerPage,
        page: tableConfig.page,
        count: tableConfig.count,
        rowsPerPageOptions: [10, 20, 50, 100],
        onChangePage: (newPage) => fetchData(newPage, tableConfig.rowsPerPage),
        onChangeRowsPerPage: (newRowsPerPage) => fetchData(0, newRowsPerPage),
        textLabels: {
            body: {
                noMatch: loading ? 'Loading...' : 'No transactions found',
            },
        },
        setTableProps: () => ({
            size: 'small',
            style: { tableLayout: 'auto', width: '100%' },
        }),
    }

    return (
        <Wrapper>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        Club Transactions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Payments from members, with filters, stats, and export
                    </Typography>
                </Box>
                <Tooltip title="Refresh">
                    <span>
                        <IconButton
                            onClick={() => fetchData(tableConfig.page, tableConfig.rowsPerPage)}
                            disabled={loading}
                            color="primary"
                        >
                            {loading ? <CircularProgress size={22} /> : <RefreshIcon />}
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                {DATE_PRESETS.map((item) => (
                    <Chip
                        key={item.id}
                        label={item.label}
                        clickable
                        color={preset === item.id ? 'primary' : 'default'}
                        variant={preset === item.id ? 'filled' : 'outlined'}
                        onClick={() => handlePreset(item.id)}
                    />
                ))}
            </Box>

            <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        label="From"
                        type="date"
                        size="small"
                        fullWidth
                        value={from}
                        onChange={(event) => {
                            setPreset('custom')
                            setFrom(event.target.value)
                        }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        label="To"
                        type="date"
                        size="small"
                        fullWidth
                        value={to}
                        onChange={(event) => {
                            setPreset('custom')
                            setTo(event.target.value)
                        }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                            label="Status"
                            value={status}
                            onChange={(event) => setStatus(event.target.value)}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="succeeded">Succeeded</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                    <TextField
                        label="Min amount"
                        type="number"
                        size="small"
                        fullWidth
                        value={minAmount}
                        onChange={(event) => setMinAmount(event.target.value)}
                        inputProps={{ min: 0, step: '0.01' }}
                    />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                    <TextField
                        label="Max amount"
                        type="number"
                        size="small"
                        fullWidth
                        value={maxAmount}
                        onChange={(event) => setMaxAmount(event.target.value)}
                        inputProps={{ min: 0, step: '0.01' }}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} mb={3}>
                {headlineStats.map((stat) => {
                    const IconComponent = stat.icon
                    return (
                        <Grid item xs={12} md={6} key={stat.title}>
                            <HeroStat>
                                <Box
                                    display="flex"
                                    alignItems="flex-start"
                                    justifyContent="space-between"
                                    gap={2}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        fontWeight={600}
                                    >
                                        {stat.title}
                                    </Typography>
                                    <IconWrapper bgcolor={stat.bg}>
                                        <IconComponent sx={{ color: stat.color }} />
                                    </IconWrapper>
                                </Box>
                                <Typography
                                    variant="h4"
                                    fontWeight={800}
                                    sx={{
                                        color: stat.color,
                                        letterSpacing: '-0.04em',
                                        mt: 1,
                                        lineHeight: 1.15,
                                    }}
                                >
                                    {stat.value}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                    {stat.helper}
                                </Typography>
                            </HeroStat>
                        </Grid>
                    )
                })}
                <Grid item xs={12}>
                    <FeePanel>
                        <Grid container>
                            {feeStats.map((stat, index) => {
                                const IconComponent = stat.icon
                                return (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={4}
                                        key={stat.title}
                                        sx={{
                                            borderRight: {
                                                sm:
                                                    index < feeStats.length - 1
                                                        ? '1px solid'
                                                        : 'none',
                                            },
                                            borderBottom: {
                                                xs:
                                                    index < feeStats.length - 1
                                                        ? '1px solid'
                                                        : 'none',
                                                sm: 'none',
                                            },
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <FeeCell>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                gap={1}
                                                mb={1}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    fontWeight={600}
                                                >
                                                    {stat.title}
                                                </Typography>
                                                <IconWrapper
                                                    bgcolor={stat.bg}
                                                    sx={{ width: 36, height: 36, borderRadius: '10px' }}
                                                >
                                                    <IconComponent
                                                        sx={{ color: stat.color, fontSize: 20 }}
                                                    />
                                                </IconWrapper>
                                            </Box>
                                            <Typography
                                                variant="h6"
                                                fontWeight={700}
                                                noWrap
                                                sx={{ color: stat.color }}
                                            >
                                                {stat.value}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {stat.helper}
                                            </Typography>
                                        </FeeCell>
                                    </Grid>
                                )
                            })}
                        </Grid>
                    </FeePanel>
                </Grid>
            </Grid>

            <Grid container spacing={2} mb={3}>
                {planBreakdown.length > 0 && (
                    <Grid item xs={12}>
                        <ChartCard>
                            <Box textAlign="center" mb={2}>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    Revenue by plan
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Gross amount for top membership plans
                                </Typography>
                            </Box>
                            <PlanPieChart
                                items={planBreakdown.map((item) => ({
                                    ...item,
                                    key: item.planId || item.name,
                                }))}
                                currency={currency}
                            />
                        </ChartCard>
                    </Grid>
                )}
            </Grid>

            <TableCard>
                <Box
                    display="flex"
                    flexWrap="wrap"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    px={3}
                    py={2}
                >
                    <TextField
                        variant="outlined"
                        size="small"
                        value={searchValue}
                        onChange={(event) => setSearchValue(event.target.value)}
                        placeholder="Search member, email or payment reference"
                        sx={{ minWidth: 280, flex: '1 1 280px', maxWidth: 460 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: searchValue ? (
                                <InputAdornment position="end">
                                    {searchValue !== debouncedSearch ? (
                                        <CircularProgress size={18} />
                                    ) : (
                                        <IconButton
                                            size="small"
                                            onClick={() => setSearchValue('')}
                                            aria-label="Clear search"
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <Button
                        variant="contained"
                        startIcon={
                            exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />
                        }
                        onClick={handleExport}
                        disabled={exporting || tableConfig.count === 0}
                    >
                        Export CSV
                    </Button>
                </Box>

                {loading && transactions.length === 0 ? (
                    <Box display="flex" flexDirection="column" alignItems="center" py={8} gap={2}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">
                            Loading transactions...
                        </Typography>
                    </Box>
                ) : !loading && transactions.length === 0 && tableConfig.count === 0 ? (
                    <Box display="flex" flexDirection="column" alignItems="center" py={8} gap={2}>
                        <InboxIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                        <Typography variant="h6" fontWeight={600}>
                            No transactions found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Try a different date range, status, or search.
                        </Typography>
                    </Box>
                ) : (
                    <MUIDataTable
                        title={`Transactions (${tableConfig.count})`}
                        data={transactions}
                        columns={columns}
                        options={tableOptions}
                    />
                )}
            </TableCard>
        </Wrapper>
    )
}

export default ClubTransactions
